import type { SvgIconProps, Theme, TypographyProps } from '@mui/material';
import { Box, IconButton, styled, SvgIcon, Typography } from '@mui/material';

/**
 * Styled components for Snackbar
 * Using MUI's styled() API for true performance optimization
 * Components are created once and reuse styles across renders
 */

/**
 * Resolves MUI theme path strings to actual color values
 * @param theme - MUI theme object
 * @param colorPath - Theme path string (e.g., 'success.main') or direct color value (e.g., '#4caf50')
 * @returns Resolved color value
 * @example
 * getResolvedColor(theme, 'success.main') // Returns '#4caf50' (or theme's success.main color)
 * getResolvedColor(theme, '#ff0000') // Returns '#ff0000' (passthrough for non-theme paths)
 */
const getResolvedColor = (theme: Theme, colorPath: string): string => {
  const [category, shade] = colorPath.split('.');

  if (category && shade && category in theme.palette) {
    const paletteCategory = theme.palette[category as keyof typeof theme.palette];
    if (paletteCategory && typeof paletteCategory === 'object' && shade in paletteCategory) {
      return (paletteCategory as Record<string, string>)[shade] || colorPath;
    }
  }

  return colorPath;
};

// ========== Shared Icon Styled Components ==========

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
export const StyledVariantIcon = styled(SvgIcon, {
  shouldForwardProp: prop => prop !== 'iconSize' && prop !== 'iconColor',
})<SvgIconProps & { iconSize: number; iconColor: string }>(({ theme, iconSize, iconColor }) => ({
  fontSize: iconSize,
  color: getResolvedColor(theme, iconColor),
  flexShrink: 0,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
})) as any;

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
export const StyledCloseIcon = styled(SvgIcon)<SvgIconProps>({
  fontSize: 20,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}) as any;

// ========== Contextual Layout Styled Components ==========

export const ContextualContainer = styled(Box)<{ borderColor: string }>(
  ({ theme, borderColor }) => ({
    display: 'flex',
    alignItems: 'center', // Center icon vertically
    gap: 16, // gap: 2 in theme = 16px
    backgroundColor: '#ffffff',
    borderLeft: `12px solid ${getResolvedColor(theme, borderColor)}`,
    borderRadius: '6px',
    padding: '20px 20px 20px 16px',
    minWidth: '400px',
    maxWidth: '600px',
    boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14)',
  }),
) as typeof Box;

export const ContextualContent = styled(Box)({
  flex: 1,
  minWidth: 0,
}) as typeof Box;

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
export const ContextualTitle = styled(Typography, {
  shouldForwardProp: prop => prop !== 'hasDetails',
})<TypographyProps & { hasDetails: boolean }>(({ hasDetails }) => ({
  color: 'rgba(0, 6, 38, 0.90)',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: 1.4,
  marginBottom: hasDetails ? 4 : 0, // 0.5 in theme = 4px
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
})) as any;

export const ContextualDetail = styled(Typography)({
  color: '#181E3B',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.5,
}) as typeof Typography;

export const ActionContainer = styled(Box)({
  display: 'flex',
  gap: 8, // gap: 1 in theme = 8px
  alignItems: 'center',
}) as typeof Box;

export const ContextualCloseButton = styled(IconButton)({
  color: 'rgba(0, 13, 77, 0.45)',
  padding: '4px',
  flexShrink: 0,
  alignSelf: 'center',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}) as typeof IconButton;

// ========== Generic Layout Styled Components ==========

export const GenericContainer = styled(Box)<{ borderColor: string }>(({ theme, borderColor }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16, // gap: 2 in theme = 16px
  backgroundColor: '#ffffff',
  borderLeft: `6px solid ${getResolvedColor(theme, borderColor)}`,
  borderRadius: '6px',
  padding: '16px',
  minWidth: '288px',
  maxWidth: '568px',
  boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14)',
})) as typeof Box;

export const GenericMessage = styled(Box)({
  flex: 1,
  color: '#181E3B',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.5,
}) as typeof Box;

export const GenericCloseButton = styled(IconButton)({
  color: 'rgba(0, 13, 77, 0.45)',
  padding: '4px',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}) as typeof IconButton;
